import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { lastValueFrom, Subject } from 'rxjs';
import { ButtonComponent } from 'src/components/button/button.component';
import {
  InputSelectComponent,
  InputSelectOption,
} from 'src/components/input/input-select.component';
import { Form, FormError } from 'src/definitions/form.definition';
import { CreateKaryawanForm } from 'src/definitions/karyawan.definition';
import { FormService } from 'src/services/form.service';
import { ToastService } from 'src/services/toast.service';
import { GlobalService } from 'src/services/global.service';
import { RestService } from 'src/services/rest.service';
import { InputNumberComponent } from 'src/components/input/input-number.component';
import { InputDateComponent } from 'src/components/input/input-date.component';

export type PositionForm = Pick<
  CreateKaryawanForm,
  | 'divisi_id'
  | 'sub_divisi_id'
  | 'level_id'
  | 'lokasi_id'
  | 'kontrak_id'
  | 'join_date'
  | 'saldo_cuti_awal'
>;

@Component({
  selector: 'step-4',
  templateUrl: './step-4.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    InputDateComponent,
    InputSelectComponent,
    InputNumberComponent,
  ],
})
export class _Step4Component implements OnInit {
  @Input()
  form!: Form<PositionForm>;

  @Input()
  formError!: FormError<PositionForm>;

  @Output()
  next = new EventEmitter<void>();

  @Output()
  back = new EventEmitter<void>();

  @Input()
  isEdit: boolean = false;

  protected divisiOptions: InputSelectOption<string, { uuid: string }>[] = [];
  protected divisiOptionsSearch: string = '';
  protected divisiOptionsFetching: boolean = true;
  protected divisiOptionsFetchingAborter$ = new Subject<void>();

  protected subDivisiOptions: InputSelectOption[] = [];
  protected subDivisiOptionsSearch: string = '';
  protected subDivisiOptionsFetching: boolean = true;
  protected subDivisiOptionsFetchingAborter$ = new Subject<void>();

  protected jabatanOptions: InputSelectOption[] = [];
  protected jabatanOptionsSearch: string = '';
  protected jabatanOptionsFetching: boolean = true;
  protected jabatanOptionsFetchingAborter$ = new Subject<void>();

  protected lokasiOptions: InputSelectOption[] = [];
  protected lokasiOptionsSearch: string = '';
  protected lokasiOptionsFetching: boolean = true;
  protected lokasiOptionsFetchingAborter$ = new Subject<void>();

  protected kontrakOptions: InputSelectOption[] = [];
  protected kontrakOptionsSearch: string = '';
  protected kontrakOptionsFetching: boolean = true;
  protected kontrakOptionsFetchingAborter$ = new Subject<void>();

  constructor(
    private restService: RestService,
    private toastService: ToastService,
    private globalService: GlobalService,
    protected formService: FormService,
  ) {}

  ngOnInit(): void {
    this._getDivisiOptions();
    this._getJabatanOptions();
    this._getLokasiOptions();
    this._getKontrakOptions();
  }

  protected _onDivisiChange() {
    this.formService.resetError(this.formError, 'divisi_id');

    const selectedDivisi = this.divisiOptions.filter((item) => {
      return item.value === this.form.value.divisi_id;
    })[0];

    if (selectedDivisi) this._getSubDivisiOptions(selectedDivisi.uuid);
  }

  protected async _getDivisiOptions() {
    this.divisiOptionsFetchingAborter$.next();

    try {
      setTimeout(() => {
        this.divisiOptions = [];
        this.divisiOptionsFetching = true;
      });

      const query = {
        page: '1',
        limit: '50',
      };
      const response = await lastValueFrom(this.restService.getAutocompleteDivisi(query));

      this.divisiOptions = response.data.data.map((item) => ({
        value: (item.id ?? 1).toString(),
        label: item.name,
        uuid: item.uuid,
      }));

      const selectedDivisi = response.data.data.filter((item) => {
        return item.id.toString() === this.form.value.divisi_id;
      })[0];

      if (selectedDivisi) this._getSubDivisiOptions(selectedDivisi.uuid);
    } catch (error) {
    } finally {
      this.divisiOptionsFetching = false;
    }
  }

  protected async _getSubDivisiOptions(divisiUuid: string) {
    this.subDivisiOptionsFetchingAborter$.next();

    try {
      setTimeout(() => {
        this.subDivisiOptions = [];
        this.subDivisiOptionsFetching = true;
      });

      const query = {
        page: '1',
        limit: '50',
      };
      const response = await lastValueFrom(
        this.restService.getAutocompleteSubDivisi(divisiUuid, query),
      );

      this.subDivisiOptions = response.data.data.map((item) => ({
        value: (item.id ?? 1).toString(),
        label: item.name,
      }));
    } catch (error) {
    } finally {
      this.subDivisiOptionsFetching = false;
    }
  }

  protected async _getJabatanOptions() {
    this.jabatanOptionsFetchingAborter$.next();

    try {
      setTimeout(() => {
        this.jabatanOptions = [];
        this.jabatanOptionsFetching = true;
      });

      const query = {
        page: '1',
        limit: '50',
      };
      const response = await lastValueFrom(this.restService.getAutocompleteJabatan(query));

      this.jabatanOptions = response.data.data.map((item) => ({
        value: item.id.toString(),
        label: item.name,
      }));
    } catch (error) {
    } finally {
      this.jabatanOptionsFetching = false;
    }
  }

  protected async _getLokasiOptions() {
    this.lokasiOptionsFetchingAborter$.next();

    try {
      setTimeout(() => {
        this.lokasiOptions = [];
        this.lokasiOptionsFetching = true;
      });

      const query = {
        page: '1',
        limit: '50',
      };
      const response = await lastValueFrom(this.restService.getAutocompleteLokasi(query));

      this.lokasiOptions = response.data.data.map((item) => ({
        value: item.id.toString(),
        label: item.name,
      }));
    } catch (error) {
    } finally {
      this.lokasiOptionsFetching = false;
    }
  }

  protected async _getKontrakOptions() {
    this.kontrakOptionsFetchingAborter$.next();

    try {
      setTimeout(() => {
        this.kontrakOptions = [];
        this.kontrakOptionsFetching = true;
      });

      const query = {
        page: '1',
        limit: '50',
      };
      const response = await lastValueFrom(this.restService.getAutocompleteKontrak(query));

      this.kontrakOptions = response.data.data.map((item) => ({
        value: item.id.toString(),
        label: item.name,
      }));
    } catch (error) {
    } finally {
      this.kontrakOptionsFetching = false;
    }
  }

  protected _next() {
    if (this.form.invalid) {
      const invalid = this.formService.validateAndGetErrorMessage(this.form, this.formError);
      this.toastService.showToast({
        summary: invalid,
        detail: 'Cek ulang isian',
        severity: 'error',
      });
      return;
    }

    this.next.emit();
  }
}
